import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from 'i18next';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">⚠️</div>
            <h2>{i18n.t('common.somethingWentWrong')}</h2>
            <p className="error-message">
              {this.state.error?.message || i18n.t('common.unexpectedError')}
            </p>
            <div className="error-actions">
              <button className="retry-btn" onClick={this.handleRetry}>
                {i18n.t('common.tryAgain')}
              </button>
              <button
                className="reload-btn"
                onClick={() => window.location.reload()}
              >
                {i18n.t('common.reloadPage')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
